import React, { useEffect } from 'react';
import { Layer, Image, Transformer, Text, Circle, Line, Rect, Group } from "react-konva";
import NormalColumn from './NormalColumns/normalColumn';
import MastColumnAsym from './MastColumns/mastColumnAsym';
import MastColumnBeam from './MastColumns/mastColumnBeam';
import MastColumnNone from './MastColumns/mastColumnNone';
import WallColumnAsym from './MastWalls/mastWallAsym';
import WallColumnBeam from './MastWalls/mastWallBeam';
import WallColumnNone from './MastWalls/mastWallNone';

export default function ColumnHandler(props) {
    const { lanterns, isMast = false, mountingType, colorList, luminaires } = props;
    const trRef = React.useRef();
    const textRef = React.useRef();
    const shapeRef = React.useRef();
    const lineRefs = React.useRef([]);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [hoverColumn, setHoverColumn] = React.useState(false);
    const [textWidth, setTextWidth] = React.useState(0);

    useEffect(() => {
        setPosition({ x: props.values.point.x, y: props.values.point.y });
    }, [props.values.point]);

    useEffect(() => {
        if (!trRef.current || !shapeRef.current) return;

        if (props.selected) {
            trRef.current.nodes([shapeRef.current]);

            trRef.current.position(shapeRef.current.position());
            trRef.current.rotation(props.positionAngle);

            trRef.current.getLayer()?.batchDraw();
            trRef.current.update()
        } else {
            trRef.current.nodes([]);
            trRef.current.getLayer()?.batchDraw();
        }
    }, [props.selected, props.positionAngle, mountingType, isMast]);

    const getDynamicFontSize = () => {
        return Math.max(.2, 10 / props.stageScale);
    };

    useEffect(() => {
        if (textRef.current) {
            const box = textRef.current.getClientRect();
            setTextWidth(box.width / props.stageScale);
        }
    }, [props.name, props.stageScale, hoverColumn]);

    const handleDragMove = (e) => {
        setPosition({ x: e.target.x(), y: e.target.y() });
    };

    const getTiltFromBeamLength = (beamLength, height = 6) => {
        const radians = Math.atan(height / beamLength);
        const tilt = 90 - (radians * (180 / Math.PI));
        return +tilt.toFixed(2);
    };


    const handleDragMoveBeam = (e) => {
        const newX = e.target.x();
        const newY = e.target.y();
        if (props.values.configuration === "Beam Aiming") {
            const updatedLanterns = props.values.lanterns.map((lantern) => {
                const { beamEndPoint } = lantern.beamAimingInfo;

                const dx = beamEndPoint.x - newX;
                const dy = beamEndPoint.y - newY;

                const newBeamLength = Math.sqrt(dx * dx + dy * dy);

                const newAngle = ((Math.atan2(dy, dx) * 180) / Math.PI + 90 + 360) % 360;
                const roundedAngle = parseFloat(newAngle.toFixed(2));

                return {
                    ...lantern,
                    positionAngle: roundedAngle,
                    beamAimingInfo: {
                        ...lantern.beamAimingInfo,
                        beamLength: newBeamLength,
                        tilt: getTiltFromBeamLength(newBeamLength, props.values.height),
                    },
                };
            });
            props.updateLanterns(updatedLanterns, props.i);

        } else if (props.values.configuration === "Asymmetrical Flood Aiming") {
            const updatedLanterns = props.values.lanterns.map((lantern) => {
                const { beamEndPoint, beamLength } = lantern.beamAimingInfo;

                const dx = beamEndPoint.x - newX;
                const dy = beamEndPoint.y - newY;

                const angle = ((Math.atan2(dy, dx) * 180) / Math.PI + 90 + 360) % 360;
                const roundedAngle = parseFloat(angle.toFixed(2));
                const angleRad = ((roundedAngle - 90) * Math.PI) / 180;

                const newEndX = newX + beamLength * Math.cos(angleRad);
                const newEndY = newY + beamLength * Math.sin(angleRad);

                return {
                    ...lantern,
                    positionAngle: roundedAngle,
                    beamAimingInfo: {
                        ...lantern.beamAimingInfo,
                        beamLength,
                        beamEndPoint: {
                            x: parseFloat(newEndX.toFixed(3)),
                            y: parseFloat(newEndY.toFixed(3)),
                        },
                    },
                };
            });

            props.updateLanterns(updatedLanterns, props.i);
        }

        setPosition({ x: newX, y: newY });
    };

    const handleDragMoveBar = (e) => {
        console.log("new bar drag move", props.values.lanterns);

        const newX = e.target.x();
        const newY = e.target.y();

        const columnAngle = props.positionAngle ?? 0;
        const angleRad = (columnAngle * Math.PI) / 180;
        const barCenter = { x: newX, y: newY };
        const barThickness = 0.4;

        const isWallMount = props.values.mountingType === "wall";
        const minAngle = (columnAngle - 90 + 360) % 360;
        const maxAngle = (columnAngle + 90) % 360;

        const angleDiff = (a, b) => {
            let d = a - b;
            while (d > 180) d -= 360;
            while (d < -180) d += 360;
            return d;
        };

        const clampAngle = (angle, min, max) => {
            const diffToMin = Math.abs(angleDiff(angle, min));
            const diffToMax = Math.abs(angleDiff(angle, max));
            return diffToMin < diffToMax ? min : max;
        };

        const clampIfNeeded = (angle) => {
            if (!isWallMount) return { clamped: false, angle };
            const isAllowed =
                minAngle < maxAngle
                    ? angle >= minAngle && angle <= maxAngle
                    : angle >= minAngle || angle <= maxAngle;

            if (isAllowed) return { clamped: false, angle };
            return { clamped: true, angle: clampAngle(angle, minAngle, maxAngle) };
        };

        const offsets = props.values.lanterns.map((l) => parseFloat(l.offset) || 0);
        const minOffset = Math.min(...offsets);
        const maxOffset = Math.max(...offsets);
        const centerOffset = (minOffset + maxOffset) / 2;

        const rotateAround = (point, center, angleRad) => {
            const translated = {
                x: point.x - center.x,
                y: point.y - center.y,
            };
            const rotated = {
                x: translated.x * Math.cos(angleRad) - translated.y * Math.sin(angleRad),
                y: translated.x * Math.sin(angleRad) + translated.y * Math.cos(angleRad),
            };
            return {
                x: center.x + rotated.x,
                y: center.y + rotated.y,
            };
        };

        const updatedLanterns = props.values.lanterns.map((lantern) => {
            const offset = (parseFloat(lantern.offset) || 0) - centerOffset;
            const armX = offset;
            const armY = -barThickness / 2;

            const baseRot = rotateAround(
                { x: barCenter.x + armX, y: barCenter.y + armY },
                barCenter,
                angleRad
            );

            const beamEndPoint = lantern.beamAimingInfo.beamEndPoint;
            const dx = beamEndPoint.x - baseRot.x;
            const dy = beamEndPoint.y - baseRot.y;

            const rawAngle = (Math.atan2(dy, dx) * 180) / Math.PI;
            let newAngle = (rawAngle + 360 + 90) % 360;

            const { clamped: wasClamped, angle: finalAngle } = clampIfNeeded(newAngle);
            const roundedAngle = parseFloat(finalAngle.toFixed(2));
            const angleRadFinal = ((finalAngle - 90) * Math.PI) / 180;

            const beamLength = lantern.beamAimingInfo.beamLength;

            let beamX = beamEndPoint.x;
            let beamY = beamEndPoint.y;

            if (wasClamped || props.values.configuration === "Asymmetrical Flood Aiming") {
                beamX = baseRot.x + beamLength * Math.cos(angleRadFinal);
                beamY = baseRot.y + beamLength * Math.sin(angleRadFinal);
            }

            const updated = {
                ...lantern,
                positionAngle: roundedAngle,
                beamAimingInfo: {
                    ...lantern.beamAimingInfo,
                    beamOrigin: {
                        x: parseFloat(baseRot.x.toFixed(3)),
                        y: parseFloat(baseRot.y.toFixed(3)),
                    },
                    beamEndPoint: {
                        x: parseFloat(beamX.toFixed(3)),
                        y: parseFloat(beamY.toFixed(3)),
                    },
                },
            };


            if (props.values.configuration === "Beam Aiming") {
                const newBeamLength = Math.sqrt(dx * dx + dy * dy);
                if (!wasClamped) {
                    updated.beamAimingInfo.beamLength = newBeamLength;
                }
                updated.beamAimingInfo.tilt = getTiltFromBeamLength(
                    updated.beamAimingInfo.beamLength,
                    props.values.height
                );
            }

            return updated;
        });

        props.updateLanterns(updatedLanterns, props.i);
        setPosition({ x: newX, y: newY });
    };

    const scaleFactor = 1 / props.stageScale;
    const baseRadius = 1;
    const circleRadius = Math.max(0.5, baseRadius * scaleFactor);


    const textPosX = position.x - (textWidth / 2);
    const textPosY = position.y - ((getDynamicFontSize() + 2.75) * 2) - 1;
    const anchorOffset = 100;

    useEffect(() => {
        if (props.selected && trRef.current && shapeRef.current) {
            trRef.current.nodes([shapeRef.current]);
            trRef.current.getLayer()?.batchDraw();
        }
    }, [props.selected, props.id, shapeRef.current]);


    return (
        <>
            {!isMast && (
                <NormalColumn
                    {...props}
                    position={position}
                    shapeRef={shapeRef}
                    handleDragMove={handleDragMove}
                    circleRadius={circleRadius}
                    getDynamicFontSize={getDynamicFontSize}
                    textPosX={textPosX}
                    textPosY={textPosY}
                />
            )}



            {isMast && mountingType === "column" && (
                <>
                    {props.values.configuration == "None" && (
                        <MastColumnNone
                            lanterns={lanterns}
                            position={position}
                            props={props}
                            circleRadius={circleRadius}
                            colorList={colorList}
                            luminaires={luminaires}
                        />
                    )}

                    {(props.values.configuration == "Beam Aiming") && (
                        <MastColumnBeam
                            lanterns={lanterns}
                            position={position}
                            props={props}
                            colorList={colorList}
                            luminaires={luminaires}
                        />
                    )}

                    {(props.values.configuration == "Asymmetrical Flood Aiming") && (
                        <MastColumnAsym
                            lanterns={lanterns}
                            position={position}
                            props={props}
                            colorList={colorList}
                            luminaires={luminaires}
                        />
                    )}

                    {hoverColumn && (
                        <Text
                            ref={textRef}
                            x={textPosX}
                            y={textPosY}
                            text={props.name}
                            fontSize={getDynamicFontSize()}
                            fill="black"
                            fontStyle="bold"
                            padding={5}
                            backgroundColor="gray"
                            opacity={0.75}
                            listening={false}
                        />
                    )}

                    {/* Central mast circle */}
                    <Circle
                        x={position.x}
                        y={position.y}
                        rotation={props.positionAngle || 0}
                        radius={circleRadius / 2}
                        fill={'lightgray'}
                        draggable={props.selected}
                        ref={shapeRef}
                        onClick={() => props.selectColumn(props.id)}
                        onTap={() => props.selectColumn(props.id)}
                        onDragMove={props.values.configuration == "None" ? handleDragMove : handleDragMoveBeam}
                        onDragEnd={(e) => { props.updateColumn(e, props.i) }}
                        onMouseEnter={() => setHoverColumn(true)}
                        onMouseLeave={() => setHoverColumn(false)}
                    />
                </>
            )}




            {isMast && mountingType === "wall" && (() => {
                const offsets = lanterns.map((l) => parseFloat(l.offset) || 0);
                const minOffset = Math.min(...offsets);
                const maxOffset = Math.max(...offsets);
                const centerOffset = (minOffset + maxOffset) / 2;
                const width = Math.max(0.4, maxOffset - minOffset) + 0.4;

                const angle = props.positionAngle || 0;
                const barThickness = 0.4;

                const barCenter = {
                    x: position.x,
                    y: position.y,
                };


                function rotateAround(point, center, angleRad) {
                    const translated = {
                        x: point.x - center.x,
                        y: point.y - center.y,
                    };

                    const rotated = {
                        x: translated.x * Math.cos(angleRad) - translated.y * Math.sin(angleRad),
                        y: translated.x * Math.sin(angleRad) + translated.y * Math.cos(angleRad),
                    };

                    return {
                        x: center.x + rotated.x,
                        y: center.y + rotated.y,
                    };
                }

                const CommonProps = {
                    position,
                    lanterns,
                    colorList,
                    luminaires,
                    props,
                    rotateAround,
                    barCenter,
                    centerOffset,
                    barThickness,
                    lineRefs,
                    updateLanterns: props.updateLanterns,
                };


                return (
                    <>
                        {/* Lantern arms + heads */}
                        {props.values.configuration === "None" && <WallColumnNone {...CommonProps} />}
                        {props.values.configuration === "Beam Aiming" && <WallColumnBeam {...CommonProps} />}
                        {props.values.configuration === "Asymmetrical Flood Aiming" && <WallColumnAsym {...CommonProps} />}



                        {/* Bar centered at position */}
                        <Rect
                            x={barCenter.x}
                            y={barCenter.y}
                            width={width}
                            height={barThickness}
                            offset={{ x: width / 2, y: barThickness / 2 }}
                            fill="lightgray"
                            rotation={angle}
                            onClick={() => props.selectColumn(props.id)}
                            onTap={() => props.selectColumn(props.id)}
                            onMouseEnter={() => setHoverColumn(true)}
                            onMouseLeave={() => setHoverColumn(false)}
                        />

                        {/* Text above position.x/y — no rotation applied */}
                        {hoverColumn && (
                            <Text
                                ref={textRef}
                                x={position.x - textWidth / 2}
                                y={position.y - ((getDynamicFontSize() + 2.75) * 2) - 1}
                                text={props.name}
                                fontSize={getDynamicFontSize()}
                                fill="black"
                                fontStyle="bold"
                                padding={5}
                                backgroundColor="gray"
                                opacity={0.75}
                                listening={false}
                            />
                        )}

                        {/* Transformer anchor, transparent and draggable */}
                        <Rect
                            x={position.x}
                            y={position.y}
                            width={0.4}
                            height={0.4}
                            offset={{ x: 0.2, y: 0.2 }}
                            ref={shapeRef}
                            rotation={angle}
                            fill="transparent"
                            draggable={props.selected}
                            onDragMove={props.values.configuration == "None" ? handleDragMove : handleDragMoveBar}
                            onDragEnd={(e) => props.updateColumn(e, props.i)}
                            onClick={() => props.selectColumn(props.id)}
                            onTap={() => props.selectColumn(props.id)}
                            onMouseEnter={() => setHoverColumn(true)}
                            onMouseLeave={() => setHoverColumn(false)}
                        />
                    </>
                );
            })()}



            {props.selected && (
                <Transformer
                    ref={trRef}
                    keepRatio={true}
                    enabledAnchors={false}
                    rotateAnchorOffset={anchorOffset}
                    onTransformEnd={(e) => {
                        const newRotation = e.currentTarget.attrs.rotation;
                        props.onRotate?.(newRotation, props.i);
                    }}
                />
            )}
        </>
    );
}
